<template>
    <v-card width="100%" class="organization-header" elevation="0">
        <div class="organization-header__layout">
            <section class="organization-header__panel organization-header__panel--brand">
                <div class="organization-header__eyebrow">{{ $t("text.company_info") }}</div>

                <div class="organization-header__brand-row">
                    <div class="organization-header__badge">{{ organizationInitial }}</div>

                    <div>
                        <h3 class="organization-header__title mb-1">{{ organizationName }}</h3>
                        <p class="organization-header__subtitle mb-0">{{ organizationNumberLabel }}</p>
                    </div>
                </div>

                <div class="organization-header__stats">
                    <article class="organization-header__stat-card">
                        <span class="organization-header__label">{{ $t("text.name") }}</span>
                        <strong class="organization-header__value">{{ organizationName }}</strong>
                    </article>

                    <article class="organization-header__stat-card">
                        <span class="organization-header__label">{{ $t("text.organization_number") }}</span>
                        <strong class="organization-header__value">{{ organizationNumber }}</strong>
                    </article>
                </div>
            </section>

            <section class="organization-header__panel organization-header__panel--address">
                <div class="organization-header__eyebrow">{{ $t("text.delivery_address") }}</div>

                <template v-if="hasDeliveryAddress">
                    <h3 class="organization-header__title mb-3">{{ primaryAddressLine }}</h3>

                    <div class="organization-header__address-grid">
                        <article class="organization-header__info-card">
                            <span class="organization-header__label">{{ $t("text.delivery_address") }}</span>
                            <strong class="organization-header__value organization-header__value--wrap">{{ primaryAddressLine }}</strong>
                        </article>

                        <article class="organization-header__info-card">
                            <span class="organization-header__label">Postal and city</span>
                            <strong class="organization-header__value organization-header__value--wrap">{{ postalCityLine }}</strong>
                        </article>

                        <article class="organization-header__info-card">
                            <span class="organization-header__label">Country</span>
                            <strong class="organization-header__value organization-header__value--wrap">{{ countryLine }}</strong>
                        </article>
                    </div>
                </template>

                <div v-else class="organization-header__empty-state">
                    <v-icon color="primary" size="22" class="mr-2">mdi-map-marker-off-outline</v-icon>
                    <span>{{ $t("text.not_specified") }}</span>
                </div>
            </section>
        </div>
    </v-card>
</template>


<script>
export default {
    name: "OrganizationHeader",
    props: {
        organization: Object,
        loading: Boolean,
    },
    components: {},

    data() {
        return {};
    },

    methods: {},

    computed: {
        organizationName() {
            return this.organization?.Name || this.$t("text.not_specified");
        },

        organizationNumber() {
            return this.organization?.OrganisationNumber || this.$t("text.not_specified");
        },

        organizationNumberLabel() {
            return `${this.$t("text.organization_number")}: ${this.organizationNumber}`;
        },

        organizationInitial() {
            const name = this.organization?.Name || "?";
            return name.charAt(0).toUpperCase();
        },

        hasDeliveryAddress() {
            return Boolean(
                this.organization?.DeliveryAdressStreet ||
                    this.organization?.DeliveryAdressZip ||
                    this.organization?.DeliveryAdressCity ||
                    this.organization?.DeliveryAdressCountry,
            );
        },

        primaryAddressLine() {
            return this.organization?.DeliveryAdressStreet || this.$t("text.not_specified");
        },

        postalCityLine() {
            const parts = [this.organization?.DeliveryAdressZip, this.organization?.DeliveryAdressCity].filter(Boolean);
            return parts.length ? parts.join(" ") : this.$t("text.not_specified");
        },

        countryLine() {
            return this.organization?.DeliveryAdressCountry || this.$t("text.not_specified");
        },
    },

    watch: {},
};
</script>

<style lang="scss" scoped>
.organization-header {
    margin: 0;
    border-radius: 8px;
    background: #ffffff;
    border: 1px solid #e0e0e0;
}

.organization-header__layout {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.organization-header__panel {
    padding: 20px;
}

.organization-header__panel--brand {
    background: #ffffff;
}

.organization-header__panel--address {
    border-left: 1px solid #e0e0e0;
}

.organization-header__eyebrow {
    margin-bottom: 10px;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--v-primary-base, #1976d2);
}

.organization-header__brand-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
}

.organization-header__badge {
    display: grid;
    place-items: center;
    width: 56px;
    height: 56px;
    border-radius: 8px;
    background: var(--v-primary-base, #1976d2);
    color: #ffffff;
    font-size: 1.4rem;
    font-weight: 700;
}

.organization-header__title {
    font-size: clamp(1.2rem, 1.8vw, 1.65rem);
    line-height: 1.15;
    color: rgba(0, 0, 0, 0.87);
}

.organization-header__subtitle {
    color: rgba(0, 0, 0, 0.6);
}

.organization-header__stats,
.organization-header__address-grid {
    display: grid;
    gap: 14px;
}

.organization-header__stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.organization-header__address-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.organization-header__stat-card,
.organization-header__info-card,
.organization-header__empty-state {
    padding: 14px;
    border-radius: 8px;
    background: #f7f9fc;
    border: 1px solid #e8edf5;
}

.organization-header__empty-state {
    display: flex;
    align-items: center;
    color: rgba(0, 0, 0, 0.6);
}

.organization-header__label {
    display: block;
    margin-bottom: 8px;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.54);
}

.organization-header__value {
    display: block;
    font-size: 1rem;
    line-height: 1.45;
    color: rgba(0, 0, 0, 0.87);
}

.organization-header__value--wrap {
    overflow-wrap: anywhere;
}

@media (max-width: 960px) {
    .organization-header__layout {
        grid-template-columns: 1fr;
    }

    .organization-header__panel--address {
        border-left: 0;
        border-top: 1px solid #e0e0e0;
    }

    .organization-header__address-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 600px) {
    .organization-header__panel {
        padding: 16px;
    }

    .organization-header__brand-row,
    .organization-header__stats {
        grid-template-columns: 1fr;
    }

    .organization-header__brand-row {
        display: grid;
        gap: 12px;
    }
}
</style>